// packages/web-ui/src/components/inline-cards/DataTableCard.tsx
//
// Generic deterministic data-table card. Renders a COMPLETE {columns, rows}
// table derived server-side from the KB (`deriveDataTable`) and delivered on
// the `kb:dataTable` SSE event. Shared by every tabular enumeration skill
// (hierarchy / weight / budget / bom / trace / safety / governance): the card
// is the authoritative, complete list, so the LLM's prose no longer has to
// transcribe rows. Pure data card, no per-card handlers — mirrors
// `ChangesetPreviewCard` / `ConnectionsTableCard`.

import type { JSX } from 'react';
import { CardShell } from './CardShell.js';
import type { DataTableEnvelopeData } from '../../state/inline-cards.js';

export type DataTableCardProps = DataTableEnvelopeData;

export function DataTableCard(props: DataTableCardProps): JSX.Element {
  const columns = props.columns;
  const rows = props.rows;
  return (
    <CardShell
      role="region"
      ariaLabel={props.title}
      cardKind="data-table"
      testId="data-table-card"
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-baseline justify-between gap-2">
          <div className="font-medium text-fg" data-testid="data-table-title">
            {props.title}
          </div>
          {props.caption !== undefined ? (
            <span
              data-testid="data-table-caption"
              className="text-[10px] font-medium uppercase tracking-wide text-taupe-700"
            >
              {props.caption}
            </span>
          ) : null}
        </div>
        <div className="overflow-x-auto">
          <table data-testid="data-table" className="w-full border-collapse text-xs">
            <thead>
              <tr className="border-b border-taupe-300 text-left text-taupe-700">
                {columns.map((col, i) => (
                  <th key={`${col}-${i}`} className="py-1 pr-3 font-medium">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr
                  key={ri}
                  data-testid="data-table-row"
                  className="border-b border-taupe-200 align-top"
                >
                  {/* Align cells to the header; a short row pads with blanks,
                      a long row is clamped, so a malformed row never shifts
                      columns. */}
                  {columns.map((_col, ci) => (
                    <td
                      key={ci}
                      className={ci === 0 ? 'py-1 pr-3 font-mono' : 'py-1 pr-3'}
                    >
                      {row[ci] !== undefined && row[ci].length > 0 ? row[ci] : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </CardShell>
  );
}
